export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_EXTENSIONS = ['pdf', 'docx', 'txt'] as const;

export type DocumentKind = (typeof ACCEPTED_EXTENSIONS)[number];

const KIND_BY_EXTENSION: Record<string, DocumentKind> = {
  pdf: 'pdf',
  docx: 'docx',
  txt: 'txt',
};

const MIME_TYPES_BY_KIND: Record<DocumentKind, readonly string[]> = {
  pdf: ['application/pdf'],
  docx: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  txt: ['text/plain'],
};

// Browsers and OSes frequently report uploads as generic or no type at all;
// those declarations carry no information, so magic bytes decide instead.
const UNKNOWN_MIME_TYPES = new Set(['', 'application/octet-stream']);

export type FileValidationResult =
  | { ok: true; kind: DocumentKind }
  | { ok: false; error: string };

export function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  if (dot < 0 || dot === fileName.length - 1) return '';
  return fileName.slice(dot + 1).toLowerCase();
}

export function acceptsFile(fileName: string): boolean {
  return extensionOf(fileName) in KIND_BY_EXTENSION;
}

/** Identifies a document by its leading bytes, independent of client metadata. */
export function sniffKind(bytes: Uint8Array): DocumentKind | null {
  if (bytes.length >= 4 && asciiPrefix(bytes, '%PDF')) return 'pdf';
  // ZIP local file header "PK\x03\x04" — the container DOCX is built on.
  if (
    bytes.length >= 4 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    bytes[2] === 0x03 &&
    bytes[3] === 0x04
  ) {
    return 'docx';
  }
  return null;
}

function asciiPrefix(bytes: Uint8Array, prefix: string): boolean {
  for (let i = 0; i < prefix.length; i += 1) {
    if (bytes[i] !== prefix.charCodeAt(i)) return false;
  }
  return true;
}

export interface ValidateFileOptions {
  maxBytes?: number;
}

/**
 * Validates an untrusted upload before any parsing: extension allow-list, size
 * cap, declared MIME when informative, and magic bytes. Never trusts
 * client-provided names or types on their own.
 */
export async function validateResumeFile(
  file: File,
  options: ValidateFileOptions = {},
): Promise<FileValidationResult> {
  const maxBytes = options.maxBytes ?? MAX_FILE_SIZE_BYTES;

  const kind = KIND_BY_EXTENSION[extensionOf(file.name)];
  if (!kind) {
    return {
      ok: false,
      error: `Unsupported file type "${file.name || 'unnamed file'}". Upload a PDF, DOCX, or TXT file.`,
    };
  }

  if (file.size === 0) {
    return { ok: false, error: `"${file.name}" is empty (0 bytes).` };
  }

  const megaBytes = Math.round(maxBytes / (1024 * 1024));
  if (file.size > maxBytes) {
    return {
      ok: false,
      error: `"${file.name}" is ${formatBytes(file.size)} — over the ${megaBytes} MB limit. Split or shrink the document and try again.`,
    };
  }

  const declaredMime = file.type.toLowerCase().split(';')[0]?.trim() ?? '';
  if (
    declaredMime &&
    !UNKNOWN_MIME_TYPES.has(declaredMime) &&
    !MIME_TYPES_BY_KIND[kind].includes(declaredMime)
  ) {
    return {
      ok: false,
      error: `Your browser reports "${file.name}" as ${declaredMime}, which does not match a ${kind.toUpperCase()} file. Nothing was read from it.`,
    };
  }

  const head = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  const actual = sniffKind(head);
  if (actual !== kind) {
    if (actual === null && kind === 'txt') {
      return { ok: true, kind: 'txt' };
    }
    if (kind === 'txt') {
      return {
        ok: false,
        error: `"${file.name}" contains binary document content, not plain text. Upload it with its real ${actual?.toUpperCase()} extension instead.`,
      };
    }
    return {
      ok: false,
      error: `"${file.name}" looks corrupt or is not a real ${kind.toUpperCase()} file — its content does not match the expected ${kind.toUpperCase()} format.`,
    };
  }

  return { ok: true, kind };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 'B';
  for (const next of units) {
    if (value < 1024) break;
    value /= 1024;
    unit = next;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${unit}`;
}

export const ACCEPT_INPUT_ATTR = ACCEPTED_EXTENSIONS.map((e) => `.${e}`).join(
  ',',
) + ',application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
